import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { PlaceBetStatus, PlacementErrorType, PlacementType } from '@frontend/sports/types/betslip';
import { BalancePropertiesService } from '@frontend/vanilla/features/balance-properties';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EditBetStatus } from 'packages/sports/web/app/src/my-bets-base/models/early-payout';
import { UiManagerActions } from 'packages/sports/web/app/src/ui-manager/ui-manager.actions';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';
import { Observable, TimeoutError, catchError, concatMap, delay, from, map, of, startWith, switchMap, timeout } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { IEditBetPlaceBetResponseDto } from '../../../model/edit-mybet/edit-bet-place-bet-response.dto';
import { BetslipPlaceBetActions, PlaceBetFailedReason } from '../../betplacement/actions';
import { IPlacedBetslipResult } from '../../betplacement/models';
import PlaceBetResponseProcessorService from '../../betplacement/services/place-bet-response-processor.service';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipError } from '../../validation/errors/betslip-error';
import { UndoCashOutFailedError } from '../../validation/errors/edit-bet/undo-cash-out-failed-error';
import { TechnicalError } from '../../validation/errors/general/technical-error';
import { NotLoggedInError } from '../../validation/errors/user/not-logged-in-error';
import BetslipErrorValidatorService from '../../validation/services/betslip-error-validator.service';
import { hasAnyErrors } from '../../validation/services/utils/betslip-errors-state-utils';
import { emptyPickErrors } from '../../validation/state';
import { EditBetActions, IPlaceBetResponsePayload } from '../actions';
import { EarlyPayoutResponseProcessorService, PayoutResultStatus } from '../services/early-payout-response-processor.service';
import { EditBetPlaceService } from '../services/edit-bet-place.service';
import { EditBetRequestService } from '../services/edit-bet-request.service';
import { EditBetTimeoutProvider } from '../services/edit-bet-timeout.provider';

@Injectable()
export class BetslipEditBetPlaceEffects {
    private static readonly defaultGetEditBetStatusDelayInSeconds = 1;
    private readonly logger: SportsRemoteLogger;

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private editMyBetPlaceService: EditBetPlaceService,
        private editMyBetRequestService: EditBetRequestService,
        private placeBetResponseProcessorService: PlaceBetResponseProcessorService,
        private betslipEarlyPayoutResponseProcessorService: EarlyPayoutResponseProcessorService,
        private betslipValidationService: BetslipErrorValidatorService,
        private editMyBetTimeoutProvider: EditBetTimeoutProvider,
        loggerFactory: LoggerFactory,
        private balanceService: BalancePropertiesService,
    ) {
        this.logger = loggerFactory.getLogger('BetslipEditBetPlaceEpic');
    }

    handleGetPlacedBetStatusEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.getPlaceBetStatus),
            switchMap((action) => {
                const payload = action.payload;

                return this.editMyBetPlaceService.getEditBetStatus(payload).pipe(
                    timeout(payload.timeout),
                    concatMap((response) => this.processEditBetPlacementResponse(response.body, payload)),
                    catchError((error: Error) => this.getExceptionalActions(error)),
                );
            }),
        );
    });

    handlePlaceBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.placeBet),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            switchMap(([action, betslipState]) => {
                return this.betslipValidationService.validateFullPreBetPlacement$(betslipState).pipe(
                    map((errorState) => {
                        return { action, betslipState, errorState };
                    }),
                );
            }),
            switchMap(({ action, betslipState, errorState }) => {
                try {
                    const hasErrors = hasAnyErrors(errorState);

                    if (hasErrors) {
                        return [
                            BetslipErrorsActions.picksErrorsCheckFinished({ errors: errorState.pickErrors }),
                            BetslipErrorsActions.betslipErrorsCheckFinished({
                                errors: errorState.betslipErrors,
                                slipErrors: errorState.slipErrors,
                                typeSlipErrors: errorState.betslipTypeErrors,
                            }),
                        ];
                    }

                    const requestTimeoutDate = this.editMyBetTimeoutProvider.getTimeoutDate();

                    const placeData = this.editMyBetRequestService.buildEditBetRequest(betslipState, action.payload.requestId);

                    return this.editMyBetPlaceService.postEditBet(placeData.request, betslipState).pipe(
                        timeout(requestTimeoutDate),
                        concatMap((response) => {
                            const guid = placeData.request.requestId;
                            const requestPicks = placeData.betPicks;
                            const placeBetStatusPayload = {
                                guid,
                                requestPicks,
                                state: betslipState,
                                timeout: requestTimeoutDate,
                                placeBetRequest: placeData.request.placeBetRequest,
                            };

                            return this.processEditBetPlacementResponse(response.body, placeBetStatusPayload);
                        }),
                        catchError((error: Error) => this.getExceptionalActions(error)),
                        startWith(BetslipActions.setState({ state: BetslipState.Placing }), BetslipPlaceBetActions.startedPlaceBet()),
                    );
                } catch (error: any) {
                    this.logger.error(error);

                    return this.getExceptionalActions(error);
                }
            }),
        );
    });

    private processPlaceResponse(response: IEditBetPlaceBetResponseDto, payload: IPlaceBetResponsePayload): IPlacedBetslipResult {
        const status = this.placeBetResponseProcessorService.getStatus(response.placeBetResponse);
        switch (status) {
            case PlaceBetStatus.Success:
            case PlaceBetStatus.Failed:
                return this.placeBetResponseProcessorService.processDone(response.placeBetResponse, {
                    requestPicks: payload.requestPicks,
                    request: payload.placeBetRequest,
                    state: payload.state,
                });
            case PlaceBetStatus.Pending:
                throw new Error('Here should be only done status');
        }
    }

    private processEditBetPendingResponse(payload: IPlaceBetResponsePayload, delayInSeconds?: number): Observable<Action> {
        const delayInMilliseconds = (delayInSeconds || BetslipEditBetPlaceEffects.defaultGetEditBetStatusDelayInSeconds) * 1000;

        return of(EditBetActions.getPlaceBetStatus({ payload })).pipe(delay(delayInMilliseconds));
    }

    private processEditBetSucceededOrFailedResponse(response: IEditBetPlaceBetResponseDto, payload: IPlaceBetResponsePayload): Action[] {
        if (!response.payoutResponse) {
            return this.getExceptionalActions(new TechnicalError());
        }

        const failedResponse = response?.placeBetResponse?.betslips?.find((betslip) => betslip.status === PlacementType.Failed);
        const placementError = failedResponse?.failed?.errors?.find((error) =>
            [
                PlacementErrorType.CoolOffPeriodNotCompleted,
                PlacementErrorType.ParallelGamePlayPrevention,
                PlacementErrorType.PlayerNotRegisteredWithCFR,
                PlacementErrorType.UserIsSelfExcluded,
                PlacementErrorType.OasisDown,
            ].includes(error.type),
        );

        if (placementError && response.payoutResponse.payoutStatus?.error) {
            response.payoutResponse.payoutStatus.error.errorMessage = placementError.translatedErrorMessage;
        }

        const payoutResult = this.betslipEarlyPayoutResponseProcessorService.processResponse(response.payoutResponse);
        if (payoutResult.status === PayoutResultStatus.Fail) {
            return this.processEditBetFailedResponse();
        } else {
            if (!response.placeBetResponse) {
                return this.getExceptionalActions(new TechnicalError());
            }
            // When editing the bet we have only one bet so if the status is not Failed it would be success. Lets go to result screen.
            const placeBetResult = this.processPlaceResponse(response, payload);

            return [
                BetslipPlaceBetActions.trackBetPlacementResult({
                    request: payload.placeBetRequest,
                    requestPicks: payload.requestPicks,
                    placeBetResult,
                    state: payload.state,
                }),
                BetslipPlaceBetActions.finishPlaceBet({
                    request: payload.placeBetRequest,
                    requestPicks: payload.requestPicks,
                    placeBetResult,
                    state: payload.state,
                }),
                UiManagerActions.addState({ state: UiStates.EditBetResult }),
            ];
        }
    }

    private processEditBetPlacementResponse(
        response: Nullable<IEditBetPlaceBetResponseDto>,
        placeBetStatusPayload: IPlaceBetResponsePayload,
    ): Observable<Action> {
        if (response === null) {
            return from(this.getExceptionalActions(new TechnicalError()));
        }

        const placementStatus = response.placementStatus;
        switch (placementStatus.status) {
            case EditBetStatus.Pending:
                return this.processEditBetPendingResponse(placeBetStatusPayload, placementStatus.delayInSeconds);
            case EditBetStatus.Succeeded:
            case EditBetStatus.Failed:
                this.balanceService.refresh();

                return from(this.processEditBetSucceededOrFailedResponse(response, placeBetStatusPayload));
            default:
                return from(this.getExceptionalActions(new TechnicalError()));
        }
    }

    private getExceptionalActions(error: Error | TimeoutError | TechnicalError): Action[] {
        const betPlacementErrors: BetslipError[] = [];

        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            betPlacementErrors.push(new NotLoggedInError());
        } else if (error instanceof TimeoutError) {
            betPlacementErrors.push(new UndoCashOutFailedError());
            this.logger.error(error, 'Edit bet failed with timeout error');
        } else {
            betPlacementErrors.push(new TechnicalError());
            this.logger.error(error, 'Edit bet failed with technical error');
        }

        return [
            BetslipPlaceBetActions.placeBetFailed({ reason: PlaceBetFailedReason.TechnicalError }),
            BetslipErrorsActions.placeBetResponseErrors({
                pickErrors: emptyPickErrors(),
                betslipErrors: betPlacementErrors,
                slipErrors: {},
            }),
            BetslipActions.setState({ state: BetslipState.Edit }),
        ];
    }

    private processEditBetFailedResponse(): Action[] {
        return [
            BetslipPlaceBetActions.placeBetFailed({ reason: PlaceBetFailedReason.BetPlacement }),
            BetslipActions.setState({ state: BetslipState.Edit }),
        ];
    }
}
